/**
 * Las fotos de un producto: la principal (`cover`) y las demás (`gallery`), en el orden en que
 * se ven. Plan `mitter-packages/docs/plans/2026-11-01-vitrina-ropa-fotos-moneda.md`.
 *
 * `cover` sigue siendo la única que leen la app cliente, el carrito y los pedidos (que la
 * congelan); `gallery` la lee la vitrina. Este repo todavía no sube a `contracts` 0.15.0, que
 * trae `IFood.gallery` y `MAX_GALLERY_PHOTOS`: hasta entonces se leen y se escriben acá, con
 * la misma forma.
 */

/**
 * Cuántas fotos además de la principal: 6 en total. **Vendrá de `contracts` 0.15.0**
 * (`MAX_GALLERY_PHOTOS`); al subir el paquete, se importa de ahí y se borra esta.
 */
export const MAX_GALLERY_PHOTOS = 5;

/** El largo máximo de una URL de foto: la regla de `cover` en las reseñas. */
export const MAX_PHOTO_URL_LENGTH = 2048;

/** Las fotos de un producto ya ordenadas: nunca `gallery` con `cover` vacío. */
export interface ProductPhotos {
  cover: string;
  gallery: string[];
}

/** Una URL `https` que se puede leer, de hasta 2048 caracteres. */
export function isPhotoUrl(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0 || value.length > MAX_PHOTO_URL_LENGTH) {
    return false;
  }
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * La regla de quien escribe: sin repetidas, sin la principal, hasta 5, y si no hay principal
 * la primera de las demás pasa a serlo. Lo que no es texto o está vacío se descarta.
 */
export function tidyPhotos(cover: string, gallery: readonly string[]): ProductPhotos {
  const rest: string[] = [];
  let main = cover || '';
  for (const url of gallery) {
    if (typeof url !== 'string' || url === '' || url === main || rest.includes(url)) {
      continue;
    }
    if (!main) {
      main = url;
      continue;
    }
    rest.push(url);
  }
  return { cover: main, gallery: rest.slice(0, MAX_GALLERY_PHOTOS) };
}

/**
 * La regla de quien lee, que no confía en el documento (lo puede haber escrito otro): `gallery`
 * ausente es `[]`, y se descarta lo que no sea una URL `https` válida, las repetidas y la igual a
 * `cover`. Después, la misma regla que al escribir. `cover` se respeta tal cual venga si es
 * texto: lo escriben también el panel y la importación por Excel.
 */
export function readPhotos(cover: unknown, gallery: unknown): ProductPhotos {
  const main = typeof cover === 'string' ? cover : '';
  const valid = Array.isArray(gallery) ? gallery.filter(isPhotoUrl) : [];
  return tidyPhotos(main, valid);
}

/** Todas, en el orden en que se ven: la principal primero. */
export function allPhotos(photos: ProductPhotos): string[] {
  return photos.cover ? [photos.cover, ...photos.gallery] : [...photos.gallery];
}

/** Agrega una foto al final (o como principal si no hay). Pasado el tope, no cambia nada. */
export function addPhoto(photos: ProductPhotos, url: string): ProductPhotos {
  if (photos.cover && photos.gallery.length >= MAX_GALLERY_PHOTOS) {
    return photos;
  }
  return tidyPhotos(photos.cover, [...photos.gallery, url]);
}

/** Quita una foto. Si era la principal, la primera de las demás pasa a serlo. */
export function removePhoto(photos: ProductPhotos, url: string): ProductPhotos {
  if (photos.cover === url) {
    return tidyPhotos('', photos.gallery);
  }
  return tidyPhotos(
    photos.cover,
    photos.gallery.filter((item) => item !== url)
  );
}

/** «Usar como principal»: la elegida pasa a `cover` y la principal toma su lugar en las demás. */
export function promotePhoto(photos: ProductPhotos, url: string): ProductPhotos {
  const index = photos.gallery.indexOf(url);
  if (index < 0) {
    return photos;
  }
  const gallery = [...photos.gallery];
  if (photos.cover) {
    gallery[index] = photos.cover;
  } else {
    gallery.splice(index, 1);
  }
  return tidyPhotos(url, gallery);
}
